import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '../../../shared/services/API'
import type { PlatformRole } from '../platformRoles'

export interface PlatformAdmin {
    id: string
    nome: string | null
    email: string
    /** Papéis do admin autenticado. */
    roles: PlatformRole[]
    /** União das permissões dos papéis — nomes tal como em `platformRoles.PERM`. */
    permissoes: string[]
}

interface PlatformAuthContextValue {
    admin: PlatformAdmin | null
    isAuthenticated: boolean
    isLoading: boolean
    login: (email: string, password: string) => Promise<void>
    logout: () => Promise<void>
    /** Volta a chamar `GET /auth/me` — usar após alterar a própria conta (papéis/nome/password). */
    refreshMe: () => Promise<void>
    /** `true` se o admin tem a permissão (ADMIN tem sempre acesso total). */
    hasPerm: (permissao: string) => boolean
    hasRole: (role: PlatformRole) => boolean
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null)

/**
 * Sessão do painel de administração da plataforma. Independente do
 * `AuthProvider` da app normal — usa os endpoints `/api/admin/auth/*` e os
 * seus próprios cookies httpOnly (`adminAccessToken`/`adminRefreshToken`).
 */
export function PlatformAuthProvider({ children }: { children: ReactNode }) {
    const [admin, setAdmin] = useState<PlatformAdmin | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        api.get<PlatformAdmin>('/api/admin/auth/me', { suppressForbiddenToast: true })
            .then((r) => { if (!cancelled) setAdmin(r.data) })
            .catch(() => { if (!cancelled) setAdmin(null) })
            .finally(() => { if (!cancelled) setIsLoading(false) })
        return () => { cancelled = true }
    }, [])

    const login = async (email: string, password: string) => {
        const response = await api.post<PlatformAdmin>('/api/admin/auth/login', { email, password })
        setAdmin(response.data)
    }

    const logout = async () => {
        try {
            await api.post('/api/admin/auth/logout', null, { headers: { 'Content-Type': null } })
        } catch {
            // segue-se para limpar o estado local mesmo que o servidor falhe
        }
        setAdmin(null)
    }

    const refreshMe = async () => {
        try {
            const r = await api.get<PlatformAdmin>('/api/admin/auth/me', { suppressForbiddenToast: true })
            setAdmin(r.data)
        } catch {
            setAdmin(null)
        }
    }

    const value = useMemo<PlatformAuthContextValue>(
        () => ({
            admin,
            isAuthenticated: !!admin,
            isLoading,
            login,
            logout,
            refreshMe,
            hasPerm: (permissao) =>
                !!admin && (admin.roles.includes('ADMIN') || admin.permissoes.includes(permissao)),
            hasRole: (role) => !!admin && admin.roles.includes(role),
        }),
        [admin, isLoading],
    )

    return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>
}

export function usePlatformAuth() {
    const ctx = useContext(PlatformAuthContext)
    if (!ctx) throw new Error('usePlatformAuth deve ser usado dentro de <PlatformAuthProvider>.')
    return ctx
}
